import React, { useRef, useState } from "react";
import { TextField, InputAdornment, IconButton, Box, Typography, Paper, Popper } from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { useFormik } from "formik";
import * as yup from "yup";

import { resetNewPassword, changePasswordAuth } from "../../Actions/AuthActions";
import {
	MyArrowBackIcon,
	MyInfoIcon,
	MyVisibilityIcon,
	MyVisibilityOffIcon,
} from "../../Assets/svg/MyIcons";
import PasswordStrengthRules, { isPasswordValid } from '../../components/Common/PasswordStrengthRules';
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { containsBadWords } from "../../utils/badWord";

const { VALIDATION } = FRONTEND_MESSAGES;

const PASSWORD_REGEX = /^[A-Za-z0-9!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/;

const HtmlTooltip = styled(({ className, ...props }) => (
	<Tooltip
		{...props}
		classes={{ popper: className }}
		arrow
		placement="bottom-end"
		enterTouchDelay={0}
	/>
))(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		maxWidth: 300,
		padding: "14px 16px",
		border: "1px solid #e2e7f0",
		borderRadius: "10px",
		backgroundColor: "#ffffff",
		color: "#34405a",
		boxShadow: "0 14px 32px -18px rgba(15, 23, 42, 0.45)",
		fontSize: "12px",
	},
	[`& .${tooltipClasses.arrow}`]: {
		color: "#ffffff",
		"&::before": {
			border: "1px solid #e2e7f0",
		},
	},
}));

const PasswordTooltipContent = () => (
	<Box>
		<Typography sx={{ color: "#172033", fontSize: 13, fontWeight: 700, mb: 0.75 }}>
			Create a secure password
		</Typography>
		<Typography sx={{ color: "#66718a", fontSize: 11.5, lineHeight: 1.55 }}>
			Use 8–20 characters with uppercase and lowercase letters, a number,
			and a special character. Spaces are not allowed.
		</Typography>
	</Box>
);

const PasswordRulesPopover = ({ anchorEl, open, password }) => (
	<Popper
		open={open && Boolean(password)}
		anchorEl={anchorEl}
		placement="bottom-start"
		modifiers={[
			{ name: "offset", options: { offset: [0, 8] } },
			{
				name: "preventOverflow",
				options: { padding: 12 },
			},
		]}
		sx={{ zIndex: 1400 }}
	>
		<Paper
			elevation={0}
			sx={{
				width: anchorEl?.clientWidth || 320,
				maxWidth: "calc(100vw - 32px)",
				borderRadius: "10px",
				background: "transparent",
			}}
		>
			<PasswordStrengthRules password={password} show inline />
		</Paper>
	</Popper>
);

const LoadingSpinner = () => (
	<span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
);

const passwordValidationSchema = yup.object().shape({
	newPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("new password"))
		.min(8, VALIDATION.MIN_LENGTH("password", 8))
		.max(20, VALIDATION.MAX_LENGTH("password", 20))
		.test(
			"bad-word",
			"New Password contains inappropriate language.",
			(value) => !containsBadWords(value || "")
		)
		.matches(PASSWORD_REGEX, VALIDATION.PASSWORD_WEAK)
		.test("no-spaces", VALIDATION.PASSWORD_WEAK, (value) => {
			if (!value) return true;
			return !/\s/.test(value);
		}),
	confirmNewPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("confirm password"))
		.test(
			"bad-word",
			"Confirm New Password contains inappropriate language.",
			(value) => !containsBadWords(value || "")
		)
		.oneOf([yup.ref("newPassword"), null], VALIDATION.PASSWORD_MISMATCH),
});

const changePasswordValidationSchema = yup.object().shape({
	oldPassword: yup
		.string()
		.test(
			"bad-word",
			"Old Password contains inappropriate language.",
			(value) => !containsBadWords(value || "")
		)
		.required(VALIDATION.REQUIRED_FIELD("old password")),
	newPassword: yup
		.string()
		.required(VALIDATION.REQUIRED_FIELD("new password"))
		.min(8, VALIDATION.MIN_LENGTH("password", 8))
		.max(20, VALIDATION.MAX_LENGTH("password", 20))
		.test(
			"bad-word",
			"New Password contains inappropriate language.",
			(value) => !containsBadWords(value || "")
		)
		.matches(PASSWORD_REGEX, VALIDATION.PASSWORD_WEAK)
		.test("no-spaces", VALIDATION.PASSWORD_WEAK, (value) => {
			if (!value) return true;
			return !/\s/.test(value);
		})
		.test(
			"not-same-as-old-password",
			"New Password must be different from the Old Password",
			function (value) {
				return value !== this.parent.oldPassword;
			}
		),
	confirmNewPassword: yup
		.string()
		.test(
			"bad-word",
			"Confirm New Password contains inappropriate language.",
			(value) => !containsBadWords(value || "")
		)
		.required(VALIDATION.REQUIRED_FIELD("confirm password"))
		.oneOf([yup.ref("newPassword"), null], VALIDATION.PASSWORD_MISMATCH),
});

const ResetPassword = ({
	email,
	accessToken,
	ip,
	userDetails,
	showSnackbar,
	currentScreen,
	setCurrentScreen,
	SCREENS,
	isEmailVerified,
	setIsEmailVerified,
}) => {
	const [loading, setLoading] = useState(false);
	const [buttonDisable, setButtonDisable] = useState(false);
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
	const [newPasswordFocused, setNewPasswordFocused] = useState(false);
	const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
	const newPasswordAnchorRef = useRef(null);
	const confirmPasswordAnchorRef = useRef(null);

	const toggleOldPasswordVisibility = () => setShowOldPassword((prev) => !prev);
	const toggleNewPasswordVisibility = () => setShowNewPassword((prev) => !prev);
	const toggleConfirmNewPasswordVisibility = () => setShowConfirmNewPassword((prev) => !prev);

	const isChangePassword = currentScreen === SCREENS.CHANGE_PASSWORD;
	const isSetPassword = currentScreen === SCREENS.SET_PASSWORD;

	const resetPasswordFormik = useFormik({
		initialValues: { newPassword: "", confirmNewPassword: "" },
		validationSchema: passwordValidationSchema,
		onSubmit: handleResetPassword,
	});

	const changePasswordFormik = useFormik({
		initialValues: { oldPassword: "", newPassword: "", confirmNewPassword: "" },
		validationSchema: changePasswordValidationSchema,
		onSubmit: handleChangePassword,
	});

	async function handleResetPassword(values) {
		if (!isPasswordValid(values.newPassword)) {
			showSnackbar("Password does not meet all requirements", "error");
			return;
		}
		setButtonDisable(true);
		setLoading(true);

		try {
			const data = { ...values, token: accessToken, ip };

			if (isSetPassword) {
				data.email = userDetails?.data?.email;
				delete data.token;
			}

			const res = await resetNewPassword(data);
			showSnackbar(res.data.message, "success");

			setIsEmailVerified(false);

			setTimeout(() => {
				setCurrentScreen(SCREENS.LOGIN);
			}, 1000);
		} catch (err) {
			showSnackbar(err.response?.data?.message, "error");
		} finally {
			setLoading(false);
			setButtonDisable(false);
		}
	}

	async function handleChangePassword(values) {
		if (!isPasswordValid(values.newPassword)) {
			showSnackbar("Password does not meet all requirements", "error");
			return;
		}
		setButtonDisable(true);
		setLoading(true);

		try {
			const res = await changePasswordAuth({ ...values, email });
			showSnackbar(res.data.message, "success");
			changePasswordFormik.resetForm();

			setIsEmailVerified(false);

			setTimeout(() => {
				setCurrentScreen(SCREENS.LOGIN);
			}, 1000);
		} catch (err) {
			showSnackbar(err.response?.data?.message, "error");
		} finally {
			setLoading(false);
			setButtonDisable(false);
		}
	}

	const formik = isChangePassword ? changePasswordFormik : resetPasswordFormik;

	const getButtonDisabled = () => {
		if (isChangePassword) {
			return (
				buttonDisable ||
				loading ||
				!changePasswordFormik.values.oldPassword ||
				!changePasswordFormik.values.newPassword ||
				!changePasswordFormik.values.confirmNewPassword ||
				!!changePasswordFormik.errors.oldPassword ||
				!!changePasswordFormik.errors.newPassword ||
				!!changePasswordFormik.errors.confirmNewPassword ||
				!isPasswordValid(changePasswordFormik.values.newPassword)
			);
		}
		return (
			buttonDisable ||
			loading ||
			!resetPasswordFormik.values.newPassword ||
			!resetPasswordFormik.values.confirmNewPassword ||
			!!resetPasswordFormik.errors.newPassword ||
			!!resetPasswordFormik.errors.confirmNewPassword ||
			!isPasswordValid(resetPasswordFormik.values.newPassword)
		);
	};

	const getButtonText = () => {
		if (isChangePassword) return "Change Password";
		if (isSetPassword) return "Set Password";
		return "Set Password";
	};

	// const getTitle = () => {
	// 	if (isChangePassword) return "Change Password";
	// 	if (isSetPassword) return "Set Password";
	// 	return null;
	// };

	const handleBack = () => {
		formik.resetForm();
		setCurrentScreen(SCREENS.LOGIN);
	};

	const renderPasswordField = (name, label, placeholder, showPassword, toggleVisibility, formikInstance, extraProps = {}) => (
		<TextField
			name={name}
			label={label}
			placeholder={placeholder}
			type={showPassword ? "text" : "password"}
			value={formikInstance.values[name]}
			onChange={formikInstance.handleChange}
			onBlur={formikInstance.handleBlur}
			error={formikInstance.touched[name] && Boolean(formikInstance.errors[name])}
			helperText={formikInstance.touched[name] && formikInstance.errors[name]}
			fullWidth
			variant="outlined"
			className="form-control fs-5 bg-transparent"
			{...extraProps}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end" className="gap-2">
						<IconButton
							type="button"
							onClick={toggleVisibility}
							edge="end"
							aria-label={showPassword ? "Hide password" : "Show password"}
						>
							{showPassword ? <MyVisibilityOffIcon /> : <MyVisibilityIcon />}
						</IconButton>
						<HtmlTooltip title={<PasswordTooltipContent />}>
							<IconButton type="button" aria-label="View password requirements">
								<MyInfoIcon />
							</IconButton>
						</HtmlTooltip>
					</InputAdornment>
				),
			}}
		/>
	);

	if (isChangePassword) {
		return (
			<>
				<div className="fv-row mb-6 fv-plugins-icon-container">
					{renderPasswordField(
						"oldPassword",
						null,
						"Old Password",
						showOldPassword,
						toggleOldPasswordVisibility,
						changePasswordFormik
					)}
				</div>

				<div ref={newPasswordAnchorRef} className="fv-row mb-6 fv-plugins-icon-container">
					{renderPasswordField(
						"newPassword",
						null,
						"New Password",
						showNewPassword,
						toggleNewPasswordVisibility,
						changePasswordFormik,
						{ onFocus: () => setNewPasswordFocused(true), onBlur: () => setNewPasswordFocused(false) }
					)}
					<PasswordRulesPopover
						anchorEl={newPasswordAnchorRef.current}
						open={newPasswordFocused}
						password={formik.values.newPassword}
					/>
				</div>

				<div ref={confirmPasswordAnchorRef} className="fv-row mb-6 fv-plugins-icon-container">
					{renderPasswordField(
						"confirmNewPassword",
						null,
						"Confirm New Password",
						showConfirmNewPassword,
						toggleConfirmNewPasswordVisibility,
						changePasswordFormik,
						{ onFocus: () => setConfirmPasswordFocused(true), onBlur: () => setConfirmPasswordFocused(false) }
					)}
					<PasswordRulesPopover
						anchorEl={confirmPasswordAnchorRef.current}
						open={confirmPasswordFocused}
						password={formik.values.confirmNewPassword}
					/>
				</div>

				<div className="d-grid">
					<button
						type="submit"
						className="btn btn-primary fs-3 h-50px mt-2"
						disabled={getButtonDisabled()}
						onClick={changePasswordFormik.handleSubmit}
					>
						{getButtonText()}
						{loading && <LoadingSpinner />}
					</button>
				</div>

				<div className="text-center mt-6">
					<button
						type="button"
						onClick={handleBack}
						className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
					>
						<MyArrowBackIcon className="back-arrow me-2" />
						<span>Back to Login</span>
					</button>
				</div>
			</>
		);
	}

	if (isSetPassword) {
		return (
			<>
				<div ref={newPasswordAnchorRef} className="fv-row mb-6 fv-plugins-icon-container">
					{renderPasswordField(
						"newPassword",
						null,
						"New Password",
						showNewPassword,
						toggleNewPasswordVisibility,
						resetPasswordFormik,
						{ onFocus: () => setNewPasswordFocused(true), onBlur: () => setNewPasswordFocused(false) }
					)}
					<PasswordRulesPopover
						anchorEl={newPasswordAnchorRef.current}
						open={newPasswordFocused}
						password={formik.values.newPassword}
					/>
				</div>

				<div ref={confirmPasswordAnchorRef} className="fv-row mb-6 fv-plugins-icon-container">
					{renderPasswordField(
						"confirmNewPassword",
						null,
						"Confirm New Password",
						showConfirmNewPassword,
						toggleConfirmNewPasswordVisibility,
						resetPasswordFormik,
						{ onFocus: () => setConfirmPasswordFocused(true), onBlur: () => setConfirmPasswordFocused(false) }
					)}
					<PasswordRulesPopover
						anchorEl={confirmPasswordAnchorRef.current}
						open={confirmPasswordFocused}
						password={formik.values.confirmNewPassword}
					/>
				</div>

				<div className="d-grid">
					<button
						type="submit"
						className="btn btn-primary fs-3 h-50px mt-2"
						disabled={getButtonDisabled()}
						onClick={resetPasswordFormik.handleSubmit}
					>
						{getButtonText()}
						{loading && <LoadingSpinner />}
					</button>
				</div>

				<div className="text-center mt-6">
					<button
						type="button"
						onClick={handleBack}
						className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
					>
						<MyArrowBackIcon className="back-arrow me-2" />
						<span>Back to Login</span>
					</button>
				</div>
			</>
		);
	}

	return (
		<>
			<div ref={newPasswordAnchorRef} className="fv-row mb-6 fv-plugins-icon-container">
				{renderPasswordField(
					"newPassword",
					null,
					"New Password",
					showNewPassword,
					toggleNewPasswordVisibility,
					resetPasswordFormik,
					{ onFocus: () => setNewPasswordFocused(true), onBlur: () => setNewPasswordFocused(false) }
				)}
				<PasswordRulesPopover
					anchorEl={newPasswordAnchorRef.current}
					open={newPasswordFocused}
					password={formik.values.newPassword}
				/>
			</div>

			<div ref={confirmPasswordAnchorRef} className="fv-row mb-6 fv-plugins-icon-container">
				{renderPasswordField(
					"confirmNewPassword",
					null,
					"Confirm New Password",
					showConfirmNewPassword,
					toggleConfirmNewPasswordVisibility,
					resetPasswordFormik,
					{ onFocus: () => setConfirmPasswordFocused(true), onBlur: () => setConfirmPasswordFocused(false) }
				)}
				<PasswordRulesPopover
					anchorEl={confirmPasswordAnchorRef.current}
					open={confirmPasswordFocused}
					password={formik.values.confirmNewPassword}
				/>
			</div>

			<div className="d-grid">
				<button
					type="submit"
					className="btn btn-primary fs-3 h-50px mt-2"
					disabled={getButtonDisabled()}
					onClick={resetPasswordFormik.handleSubmit}
				>
					{getButtonText()}
					{loading && <LoadingSpinner />}
				</button>
			</div>

			<div className="text-center mt-6 pe-4">
				<button
					type="button"
					onClick={handleBack}
					className="back-to-login-btn btn btn-link text-decoration-none fw-bold fs-5 text-primary cursor-pointer position-relative p-0 border-0 bg-transparent align-items-center"
				>
					<MyArrowBackIcon className="back-arrow me-2" />
					<span>Back to Login</span>
				</button>
			</div>
		</>
	);
};

export default ResetPassword;